import React, { useEffect, useMemo, useState } from 'react';
import { Layers, RefreshCw } from 'lucide-react';
import ShopifySyncStatus from '../../components/ShopifySyncStatus';

export default function ShopifyCustomerCategorySegments() {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);

  const refresh = async () => {
    try {
      setLoading(true);
      const brandId = typeof window !== 'undefined' ? localStorage.getItem('currentBrandId') : null;
      if (!brandId) { setRows([]); return; }
      const headers = { 'X-Brand-ID': brandId };
      const base = (typeof window !== 'undefined' && window.location.hostname === 'localhost') ? '' : 'https://api.glowytics.com';
      const res = await fetch(`${base}/api/shopify/customers/category-monthly`, { credentials: 'include', headers });
      const js = await res.json().catch(() => ({}));
      const data = res.ok && js?.rows ? js.rows : [];
      setRows(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const categorySegments = useMemo(() => {
    const byCategory = new Map();
    for (const r of rows || []) {
      const category = r.category_name || 'Unknown';
      const cid = r.customer_id;
      const orders = Number(r.orders_count || 0);
      if (!cid) continue;
      if (!byCategory.has(category)) byCategory.set(category, new Map());
      const perCust = byCategory.get(category);
      perCust.set(cid, (perCust.get(cid) || 0) + orders);
    }
    const result = [];
    for (const [category, perCust] of byCategory.entries()) {
      let ge1 = 0, ge2 = 0, ge3 = 0, ge4 = 0;
      for (const total of perCust.values()) {
        if (total >= 1) ge1++;
        if (total >= 2) ge2++;
        if (total >= 3) ge3++;
        if (total >= 4) ge4++;
      }
      result.push({ category, counts: { ge1, ge2, ge3, ge4 } });
    }
    result.sort((a, b) => (b.counts.ge1 - a.counts.ge1) || a.category.localeCompare(b.category));
    return result;
  }, [rows]);

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6 relative">
      <ShopifySyncStatus />
      {loading ? (
        <div className="absolute inset-0 bg-white/60 backdrop-blur-sm flex items-center justify-center z-10">
          <div className="flex items-center gap-3 text-gray-700">
            <div className="w-6 h-6 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin" />
            <span className="text-sm">Loading...</span>
          </div>
        </div>
      ) : null}

      <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/shopify_glyph.svg" alt="Shopify" className="w-10 h-10 object-contain rounded-lg" />
            <h1 className="text-xl font-bold text-gray-900">Customer Category Segments</h1>
          </div>
          <div className="flex items-center gap-2">
            <button
              className={`p-2 text-gray-600 rounded-xl hover:bg-gray-100 transition-colors ${loading ? 'opacity-50' : ''}`}
              onClick={refresh}
              title="Refresh"
              disabled={loading}
            >
              <RefreshCw size={18} />
            </button>
          </div>
        </div>
      </div>

      <div className="bg-white border rounded-2xl p-4 shadow-sm">
        {categorySegments.length === 0 ? (
          <div className="text-sm text-gray-500">No data.</div>
        ) : (
          <div className="space-y-4">
            {categorySegments.map(({ category, counts }) => (
              <div key={category} className="border rounded-xl p-3">
                <div className="flex items-center justify-between mb-2">
                  <div className="font-semibold text-gray-900 flex items-center gap-2">
                    <Layers size={16} className="text-indigo-600" />
                    <span>{category}</span>
                  </div>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div className="border rounded-lg p-3 bg-indigo-50/40">
                    <div className="text-xs text-gray-600">At least 1 order</div>
                    <div className="text-xl font-bold text-gray-900 mt-1">{counts.ge1.toLocaleString()}</div>
                  </div>
                  <div className="border rounded-lg p-3 bg-indigo-50/40">
                    <div className="text-xs text-gray-600">At least 2 orders</div>
                    <div className="text-xl font-bold text-gray-900 mt-1">{counts.ge2.toLocaleString()}</div>
                  </div>
                  <div className="border rounded-lg p-3 bg-indigo-50/40">
                    <div className="text-xs text-gray-600">At least 3 orders</div>
                    <div className="text-xl font-bold text-gray-900 mt-1">{counts.ge3.toLocaleString()}</div>
                  </div>
                  <div className="border rounded-lg p-3 bg-indigo-50/40">
                    <div className="text-xs text-gray-600">At least 4 orders</div>
                    <div className="text-xl font-bold text-gray-900 mt-1">{counts.ge4.toLocaleString()}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}


